import React from "react";
import { TextField } from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";

import PrimaryButton from "../../../widgets/Buttons/PrimaryButton";
import SecondaryButton from "../../../widgets/Buttons/SecondaryButton";
import CustomModal from "../../../widgets/Modal/CustomModal";
import CustomTable from "../../../widgets/Table/CustomTable";
import ErrorSnackbar from "../../../widgets/Snackbar/ErrorSnackbar";
import SuccessSnackbar from "../../../widgets/Snackbar/SuccessSnackbar";
import useCallManagementActions from "../hooks/useCallManagementActions";
import styles from "../StudentDetails.module.scss";

const CallManagementCard = ({ uamId, adminId }) => {
  const {
    isCallLogsModalOpen,
    setIsCallLogsModalOpen,
    viewNotesText,
    setViewNotesText,
    viewUrlText,
    setViewUrlText,
    notesModal,
    closeNotesModal,
    setNotesText,
    errorSnackbar,
    successSnackbar,
    closeError,
    closeSuccess,
    callLogs,
    isLoadingLogs,
    isUpsertingNotes,
    handleUpsertNotes,
  } = useCallManagementActions({ uamId, adminId });

  const callLogColumns = [
    {
      id: "call_id",
      label: "Call ID",
      cellClassName: styles.fontWeightSemiBold,
      render: (row) => row.callId || row.call_id || "-",
    },
    {
      id: "start_time",
      label: "Start Time",
      render: (row) => {
        const time = row.startTime || row.start_time;
        return time ? new Date(time).toLocaleString() : "-";
      },
    },
    {
      id: "end_time",
      label: "End Time",
      render: (row) => {
        const time = row.endTime || row.end_time;
        return time ? new Date(time).toLocaleString() : "-";
      },
    },
    {
      id: "status_id",
      label: "Status ID",
      cellClassName: styles.fontWeightSemiBold,
      render: (row) => row.statusId || row.status_id || "-",
    },
    {
      id: "notes",
      label: "Notes",
      render: (row) => (
        <div className={styles.inlineActions}>
          <SecondaryButton
            className={styles.compactButton}
            onClick={() => setViewNotesText(row.notes)}
            disabled={!row.notes}
          >
            View
          </SecondaryButton>
        </div>
      ),
    },
    {
      id: "recording_url",
      label: "Recording",
      render: (row) => {
        const url = row.recordingUrl || row.recording_url;
        return (
          <SecondaryButton
            className={styles.compactButton}
            onClick={() => setViewUrlText(url)}
            disabled={!url}
          >
            View
          </SecondaryButton>
        );
      },
    },
  ];

  return (
    <>
      <PrimaryButton
        startIcon={<HistoryIcon />}
        onClick={() => setIsCallLogsModalOpen(true)}
        className={`${styles.actionButtonLarge} ${styles.actionButtonNeutral}`}
      >
        View Call Logs
      </PrimaryButton>

      <CustomModal
        open={isCallLogsModalOpen}
        onClose={() => setIsCallLogsModalOpen(false)}
        title="Call Logs"
        maxWidth="md"
        actions={
          <SecondaryButton onClick={() => setIsCallLogsModalOpen(false)}>
            Close
          </SecondaryButton>
        }
      >
        <CustomTable
          columns={callLogColumns}
          data={callLogs?.data || callLogs?.items || []}
          isLoading={isLoadingLogs}
          noDataMessage="No call logs found."
          className={styles.plainTable}
        />
      </CustomModal>

      <CustomModal
        open={!!viewNotesText}
        onClose={() => setViewNotesText("")}
        title="View Call Notes"
        actions={
          <SecondaryButton onClick={() => setViewNotesText("")}>
            Close
          </SecondaryButton>
        }
      >
        <div className={styles.notesSurface}>{viewNotesText}</div>
      </CustomModal>

      <CustomModal
        open={notesModal.open}
        onClose={closeNotesModal}
        title="Edit Call Notes"
        actions={
          <>
            <SecondaryButton onClick={closeNotesModal}>Cancel</SecondaryButton>
            <PrimaryButton
              onClick={handleUpsertNotes}
              isLoading={isUpsertingNotes}
            >
              Save
            </PrimaryButton>
          </>
        }
      >
        <div className={styles.modalFieldSection}>
          <TextField
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            placeholder="Enter notes..."
            value={notesModal.text}
            slotProps={{
              htmlInput: { maxLength: 200 },
              formHelperText: { className: styles.helperTextRight },
            }}
            onChange={(e) => setNotesText(e.target.value)}
            helperText={`${notesModal.text.length}/200 characters`}
          />
        </div>
      </CustomModal>

      <CustomModal
        open={!!viewUrlText}
        onClose={() => setViewUrlText("")}
        title="Recording URL"
        actions={
          <SecondaryButton onClick={() => setViewUrlText("")}>
            Close
          </SecondaryButton>
        }
      >
        <div className={styles.urlSurface}>
          <a href={viewUrlText} target="_blank" rel="noopener noreferrer">
            {viewUrlText}
          </a>
        </div>
      </CustomModal>

      <ErrorSnackbar
        open={errorSnackbar.open}
        message={errorSnackbar.message}
        onClose={closeError}
      />
      <SuccessSnackbar
        open={successSnackbar.open}
        message={successSnackbar.message}
        onClose={closeSuccess}
      />
    </>
  );
};

export default CallManagementCard;
